import { useCallback, useEffect, useMemo, useState } from 'react';
import { Combobox, Field, Option, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { useStyles } from '../styles';
import { useUserFieldHook } from './use-user-field-hook';
import { useAutomationRuleActions } from '../../use-actions';
import { ActionItemType, SendEmailNotificationActionType } from '../../types';
import ClearebleButtonInput from 'src/components/shared/cleareble-button-input';

interface Props {
  id?: string;
  actionId: string;
  validationError: string | null;
	isMultiSelect?: boolean;
}

const clearTimeoutHandler = (debounce: NodeJS.Timeout | null) => {
	if (debounce) {
		clearTimeout(debounce);
	}
};

export default function UserFieldInput(props: Props) {
	const classes = useStyles();
	const userFieldHook = useUserFieldHook();
	const {actions, setActions, resetValidationErrors} = useAutomationRuleActions();
  
	let debounce: NodeJS.Timeout | null = null;

	const [loading, setLoading] = useState(false);
	const [onInitialLoading, setOnInitialLoading] = useState(false);
	const [initialOptions, setInitialOptions] = useState<Array<{ value: string; label: string }>>([]);
	const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
	
	const [status, setStatus] = useState<'searching' | 'selected'>('selected');
	const [text, setText] = useState<string>('');

	const current: SendEmailNotificationActionType = useMemo(() => {
		const foundActions = actions.find(item => item.id === props.actionId) as ActionItemType;
		return foundActions!.sendEmailNotification as SendEmailNotificationActionType;
	}, [actions, props.actionId]);

	const onChangeText = useCallback((value: string) => {
		setStatus('searching');
		setText(value);
		
		clearTimeoutHandler(debounce);
	
		if (value === '') {
			userFieldHook.getIdentityOptions().then((resultOptions) => {
				setOptions([
					...resultOptions,
				]);
				setLoading(false);
			});
		}
		
		if (value !== '') {
			setLoading(true);
			debounce = setTimeout(() => {
				userFieldHook.searchIdentityOptions(value)
					.then((resultOptions) => {
						setOptions([
							...resultOptions,
						]);

						const itIsNotInInitialOptions: Array<{label: string; value: string}> = resultOptions.filter((resultOption) => {
							return !initialOptions.some(initialOptions => initialOptions.value === resultOption.value);
						});

						setInitialOptions((oldInitialOptions) => [...oldInitialOptions, ...itIsNotInInitialOptions]);
					})
					.finally(() => {
						setLoading(false);
					});
			}, 750);
		}
	}, [initialOptions, options]);

	const setOptionSelect = useCallback((value: string) => {
		if (value) {
			resetValidationErrors();
			const foundAction = actions.find(item => item.id === props.actionId);
			setStatus('selected');
	
			if (foundAction) {
				if (props.isMultiSelect) {
					setActions(oldActions => {
						const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
						const others = oldActions.filter(item => item.id !== props.actionId);

						const selectedValues = current.sendEmailNotification!.to.includes(value) ?
							current.sendEmailNotification!.to.filter(item => item !== value) :
							[...current.sendEmailNotification!.to, value];

						return [...others, {
							...current, sendEmailNotification: {
								...current.sendEmailNotification as SendEmailNotificationActionType,
								to: selectedValues
							}
						}];
					});
				}

				if (!props.isMultiSelect) {
					setActions(oldActions => {
						const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
						const others = oldActions.filter(item => item.id !== props.actionId);
		
						return [...others, {
							...current, sendEmailNotification: {
								...current.sendEmailNotification as SendEmailNotificationActionType,
								to: [value]
							}
						}];
					});
				}
			}
		}
	}, [
		props.isMultiSelect,
		initialOptions
	]);

	const clearOptionSelect = useCallback(() => {
		const foundAction = actions.find(item => item.id === props.actionId);
		setStatus('selected');

		if (foundAction) {
			setActions(oldActions => {
				const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
				const others = oldActions.filter(item => item.id !== props.actionId);

				return [...others, {
					...current, sendEmailNotification: {
						...current.sendEmailNotification as SendEmailNotificationActionType,
						to: []
					}
				}];
			});
		}
	}, []);

	const loadInitialOptions = useCallback(async () => {
		if (initialOptions.length > 0) {
			setOptions(initialOptions);
		}

		if (!initialOptions.length) {
			userFieldHook.getIdentityOptions().then((resultOptions) => {
				const list = [
					...resultOptions,
				];
	
				setInitialOptions(list);
				setOptions(list);
			});
		}
	}, [initialOptions]);
  
	const valueRender = useMemo(() => {
		if (current && current.to && status === 'selected') {
			return options.filter((item) => current.to.includes(item.value)).map((item) => item.label).join(', ');
		}

		if (text) {
			return text;
		}

		return '';
	}, [text, current, options, status]);

	const selectedOptions = useMemo(() => {
		const filteredValues = options.filter(item => current.to.includes(item.value));

		if (current.to && filteredValues.length > 0) {
			return filteredValues.map(item => item.value);
		}

		return [];
	}, [current, options]);

	useEffect(() => {
		setOnInitialLoading(true);
		const foundAction = actions.find(item => item.id === props.actionId);
		const asyncHandler = async () => {
			if(
				foundAction && 
				foundAction?.sendEmailNotification && 
				!foundAction.sendEmailNotification.to.length
			) {
				await loadInitialOptions();
			}
	
			if (
				foundAction && 
				foundAction?.sendEmailNotification &&
				foundAction.sendEmailNotification.to.length > 0
			) {
				const resultOptions = await userFieldHook.getIdentityOptions();
				const list = [
					...resultOptions
				];

				await Promise.allSettled(foundAction.sendEmailNotification.to.map(async (item) => {
					const result = await userFieldHook.getIdentityById(item);
					if (list.every(item => item.value !== result.value) && result) {
						list.push(result);
					}
				}));

				setOptions(list);
			}

			setOnInitialLoading(false);
		};

		asyncHandler();
	}, []);

	if (onInitialLoading) {
		return (
			<Skeleton className={classes.skeletonRoot}>
				<SkeletonItem className={classes.skeletonItem} />
			</Skeleton>
		);
	}
  
	return (
		<Field
			className={classes.dropdownRoot}
			validationMessage={props.validationError}
		>
			<Combobox
				clearable={!props.isMultiSelect}
				clearIcon={(
					<ClearebleButtonInput onClick={clearOptionSelect} />
				)}
				inputMode='search'
				autoComplete='off'
				multiselect={props.isMultiSelect}
				className={classes.dropdown}
				appearance='outline'
				placeholder='Choose a value'
				positioning="below-start"
				value={valueRender}
				selectedOptions={selectedOptions}
				onOptionSelect={(_, data) => {
					setOptionSelect(data.optionValue as string);
				}}
				onChange={(ev) => {
					onChangeText(ev.target.value);
				}}
				onBlur={() => {
					setStatus('selected');
					loadInitialOptions();
				}}
				multiple
			>
				<div style={{maxHeight: 240}}>
					{!loading && options.map((item, idx) => (
						<Option key={idx} value={item.value}>{item.label}</Option>
					))}
					{loading && <Option value='loading'>Loading...</Option>}
				</div>
			</Combobox>
		</Field>
	);
}